import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { CreateProductFormData, OrganizationProduct, ProductDiscount } from '../../types/product';
import {
  CubeIcon,
  WrenchScrewdriverIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { useOrganizations } from '../../hooks/useOrganizations';
import { DatePicker } from '../common/DatePicker';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from 'contexts/LanguageContext';

interface ProductFormProps {
  onSubmit: (data: CreateProductFormData) => void;
  initialData?: Partial<CreateProductFormData>;
  onCancel: () => void;
  isOpen: boolean;
}

interface FormSection {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const FORM_SECTIONS: Record<string, FormSection> = {
  type: {
    title: 'Product Type',
    icon: CubeIcon,
    color: 'from-blue-500 to-indigo-600'
  },
  details: {
    title: 'Product Details',
    icon: TagIcon,
    color: 'from-emerald-500 to-teal-600'
  },
  pricing: {
    title: 'Pricing',
    icon: CurrencyDollarIcon,
    color: 'from-purple-500 to-pink-600'
  }
};

interface PriceListFormProps {
  organizationId: string;
  value: OrganizationProduct;
  onChange: (value: OrganizationProduct) => void;
  onRemove: () => void;
}

const PriceListForm: React.FC<PriceListFormProps> = ({ organizationId, value, onChange, onRemove }) => {
  const handleDiscountAdd = () => {
    onChange({
      ...value,
      discounts: [
        ...value.discounts,
        {
          name: '',
          type: 'percentage',
          value: 0,
          startDate: new Date(),
          endDate: new Date(),
        }
      ]
    });
  };

  const handleDiscountRemove = (index: number) => {
    onChange({
      ...value,
      discounts: value.discounts.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg bg-white border border-gray-200 shadow-sm">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
          <input
            type="number"
            value={value.price}
            onChange={e => onChange({ ...value, price: e.target.value })}
            className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
          <select
            value={value.currency}
            onChange={e => onChange({ ...value, currency: e.target.value })}
            className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="HUF">HUF</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Tax Rate (%)</label>
          <input
            type="number"
            value={value.taxRate}
            onChange={e => onChange({ ...value, taxRate: e.target.value })}
            className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Valid From</label>
          <DatePicker
            selected={value.validFrom}
            onChange={date => onChange({ ...value, validFrom: date || new Date() })}
            className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Valid Until</label>
          <DatePicker
            selected={value.validUntil}
            onChange={date => onChange({ ...value, validUntil: date || new Date() })}
            className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Discounts Section */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <h4 className="text-xs font-medium text-gray-700">Discounts</h4>
          <button
            type="button"
            onClick={handleDiscountAdd}
            className="flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="w-3 h-3" />
            <span>Add Discount</span>
          </button>
        </div>

        <div className="space-y-2">
          {value.discounts.map((discount, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 p-2 rounded bg-gray-50 border border-gray-200">
              <div className="col-span-2">
                <input
                  placeholder="Name"
                  value={discount.name}
                  onChange={e => {
                    const newDiscounts = [...value.discounts];
                    newDiscounts[index] = { ...discount, name: e.target.value };
                    onChange({ ...value, discounts: newDiscounts });
                  }}
                  className="block w-full bg-white border border-gray-300 rounded text-xs py-1 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="col-span-2">
                <select
                  value={discount.type}
                  onChange={e => {
                    const newDiscounts = [...value.discounts];
                    newDiscounts[index] = { ...discount, type: e.target.value as 'percentage' | 'fixed' };
                    onChange({ ...value, discounts: newDiscounts });
                  }}
                  className="block w-full bg-white border border-gray-300 rounded text-xs py-1 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              <div className="col-span-2">
                <input
                  type="number"
                  placeholder="Value"
                  value={discount.value}
                  onChange={e => {
                    const newDiscounts = [...value.discounts];
                    newDiscounts[index] = { ...discount, value: Number(e.target.value) };
                    onChange({ ...value, discounts: newDiscounts });
                  }}
                  className="block w-full bg-white border border-gray-300 rounded text-xs py-1 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="col-span-2">
                <DatePicker
                  selected={discount.startDate}
                  onChange={date => {
                    const newDiscounts = [...value.discounts];
                    newDiscounts[index] = { ...discount, startDate: date || new Date() };
                    onChange({ ...value, discounts: newDiscounts });
                  }}
                  className="block w-full bg-white border border-gray-300 rounded text-xs py-1 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
                  placeholderText="Start Date"
                />
              </div>
              <div className="col-span-2">
                <DatePicker
                  selected={discount.endDate}
                  onChange={date => {
                    const newDiscounts = [...value.discounts];
                    newDiscounts[index] = { ...discount, endDate: date || new Date() };
                    onChange({ ...value, discounts: newDiscounts });
                  }}
                  className="block w-full bg-white border border-gray-300 rounded text-xs py-1 px-2 text-gray-800 focus:ring-primary-500 focus:border-primary-500"
                  placeholderText="End Date"
                />
              </div>
              <div className="col-span-1 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => handleDiscountRemove(index)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProductForm: React.FC<ProductFormProps> = ({
  onSubmit,
  initialData,
  onCancel,
  isOpen
}) => {
  const { organizations } = useOrganizations();
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<CreateProductFormData>({
    defaultValues: initialData || {
      type: 'product',
      organizations: [],
      isActive: true
    }
  });
  const { t } = useLanguage();


  useEffect(() => {
    if (initialData) {
      Object.entries(initialData).forEach(([key, value]) => {
        setValue(key as keyof CreateProductFormData, value);
      });
    }
  }, [initialData, setValue]);

  const productType = watch('type');
  const selectedOrgs = watch('organizations') || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? t('products.form.edit') : t('products.form.add')}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        {/* Type Selection */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex">
            <div className={`p-4 bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center rounded-l-lg`}>
              <CubeIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">{t('products.form.type.title')}</h3>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setValue('type', 'product')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded border ${productType === 'product'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-700 font-medium'
                    : 'border-gray-300 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                >
                  <CubeIcon className="w-5 h-5" />
                  <span className="text-xs">{t('products.form.type.product')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setValue('type', 'service')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded border ${productType === 'service'
                    ? 'bg-purple-500/20 border-purple-500 text-purple-700 font-medium'
                    : 'border-gray-300 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                >
                  <WrenchScrewdriverIcon className="w-5 h-5" />
                  <span className="text-xs">{t('products.form.type.service')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex">
            <div className={`p-4 bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center rounded-l-lg`}>
              <TagIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">{t('products.form.basicInfo.title')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    {t('products.form.basicInfo.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('name', { required: true })}
                    className={`block w-full bg-white border rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    {t('products.form.basicInfo.code')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('code', { required: true })}
                    className={`block w-full bg-white border rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.code ? 'border-red-500' : 'border-gray-300'}`}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700">
                    {t('products.form.basicInfo.description')}
                  </label>
                  <textarea
                    {...register('description')}
                    rows={3}
                    className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    {t('products.form.basicInfo.unit')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('unit', { required: true })}
                    placeholder={t('common.placeholders.unitPlaceholder')}
                    className={`block w-full bg-white border rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.unit ? 'border-red-500' : 'border-gray-300'}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Organizations and Pricing */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex">
            <div className={`p-4 bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center rounded-l-lg`}>
              <CurrencyDollarIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">{t('products.form.pricing.title')}</h3>
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {organizations.map(org => (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => {
                        const isSelected = selectedOrgs.some(o => o.organizationId === org.id);
                        if (isSelected) {
                          setValue('organizations', selectedOrgs.filter(o => o.organizationId !== org.id));
                        } else {
                          setValue('organizations', [...selectedOrgs, {
                            organizationId: org.id,
                            price: '0',
                            currency: 'HUF',
                            taxRate: '0',
                            validFrom: new Date(),
                            validUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
                            discounts: []
                          }]);
                        }
                      }}
                      className={`flex items-center space-x-2 px-2 py-1 rounded border ${selectedOrgs.some(o => o.organizationId === org.id)
                        ? 'bg-primary-500/20 border-primary-500 text-primary-700 font-medium'
                        : 'border-gray-300 text-gray-600 hover:border-primary-300 hover:bg-primary-50'
                        }`}
                    >
                      <BuildingOffice2Icon className="w-4 h-4" />
                      <span className="text-xs">{org.name}</span>
                    </button>
                  ))}
                </div>

                {selectedOrgs.map((org, index) => {
                  const organization = organizations.find(o => o.id === org.organizationId);
                  return organization ? (
                    <div key={org.organizationId} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded border border-gray-300 bg-gray-50">
                      <div className="md:col-span-3">
                        <h4 className="text-sm font-medium text-gray-800">{organization.name}</h4>
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          {t('products.form.pricing.price')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          {...register(`organizations.${index}.price` as const, { required: true })}
                          className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          {t('products.form.pricing.currency')} <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register(`organizations.${index}.currency` as const, { required: true })}
                          className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="HUF">HUF</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          {t('products.form.pricing.taxRate')} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="1"
                          {...register(`organizations.${index}.taxRate` as const, { required: true })}
                          className="block w-full bg-white border border-gray-300 rounded text-xs py-1.5 px-2 text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 border border-gray-300 text-xs text-gray-700 rounded hover:bg-gray-100 transition-all duration-200 font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-2 text-xs rounded-md hover:from-primary-600 hover:to-primary-700 transition-all duration-200 font-medium shadow-lg shadow-primary-500/25"
          >
            {initialData ? t('products.form.buttons.update') : t('products.form.buttons.add')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductForm; 